// FairFix analytics — single, lightweight wrapper around gtag.js (GA4) AND
// Meta (Facebook) Pixel. Both scripts are loaded from public/index.html so
// this module only *sends* events; it never loads scripts itself.
//
// One entry point, two platforms:
//   trackEvent(name, params) → fires the GA4 custom event verbatim AND, if
//   the event name maps to a Meta standard event, fires that on the Pixel too.
//
// Owner-swap points:
//   • GA4  ID → GA4_MEASUREMENT_ID  in constants.js  (mirror in index.html)
//   • Meta ID → META_PIXEL_ID       in constants.js  (mirror in index.html)
//
// Respect for user privacy:
//   • If the browser sends Do Not Track ("1"), events are silently dropped.
//   • No third-party libraries beyond the two official snippets.

// GA4 custom event name → Meta standard event name.
// Only listed events fire on Meta; others (e.g. facebook_click) are GA4-only.
//
// IMPORTANT: The site fires TWO GA4 events per contact interaction — a
// canonical event (call_click / sms_click / quote_submit) and a location-
// tagged alias (contact_call / contact_text / contact_quote / cta_quote_hero)
// used for GA4 funnel reporting. Meta Pixel MUST fire only ONCE per user
// action or "Lead" / "Contact" conversion counts double in Ads Manager and
// bid optimisation goes off the rails.
//
// Rule: only the CANONICAL event maps to Meta. The location-tagged aliases
// stay GA4-only.
const GA_TO_META_STANDARD_EVENT = {
  // Canonical contact events — one Meta event per user action.
  call_click: 'Contact',
  sms_click: 'Contact',
  quote_submit: 'Lead',
  quote_form_submit: 'Lead',
  // Catalogue funnel (Showroom build)
  gallery_open: 'ViewContent',
  design_open: 'ViewContent',
  enquire_open: 'InitiateCheckout',
  sms_design_click: 'Contact',
  // NOTE: contact_call / contact_text / contact_quote / cta_quote_hero /
  // quote_cta_click / cta_view_work / catalogue_view / enquire_submit are
  // GA4-only aliases. A design enquiry already emits canonical quote_submit.
  // Do NOT add them to this map — the canonical events above already cover
  // Meta and duplicating would double-count Contact/Lead conversions.
};

const isDNT = () => {
  if (typeof navigator === 'undefined') return false;
  const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
};

// Repeat the hostname check here so event dispatch stays safe even when this
// module is rendered without public/index.html (for example a component preview).
const isLocalPreview = () => {
  const host = window.location.hostname.toLowerCase().replace(/\.$/, '');
  return host === 'localhost' || host.endsWith('.localhost') ||
    /^127(?:\.\d{1,3}){3}$/.test(host) || host === '[::1]' || host === '::1';
};

/**
 * Fire an analytics event on GA4 and (when mapped) on the Meta Pixel.
 * Safe to call before either script has loaded (both use internal queues).
 * Safe on server-render (no-op). Never throws.
 *
 * @param {string} name   GA4 event name (e.g. "call_click"). Preserved for GA4.
 * @param {object} params Optional event params. Passed to GA4 as-is and to Meta
 *                        as the event's custom_data object.
 */
export const trackEvent = (name, params = {}) => {
  try {
    if (typeof window === 'undefined') return;
    if (window.__FAIRFIX_TRACKING_DISABLED__ || isLocalPreview()) return;
    if (isDNT()) return;
    // Build-time pre-render (scripts/prerender.js) runs headless Chrome —
    // never record events from those renders.
    if (navigator.webdriver) return;

    // Google Analytics 4
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }

    // Meta Pixel — only if this event maps to a standard event
    const metaName = GA_TO_META_STANDARD_EVENT[name];
    if (metaName && typeof window.fbq === 'function') {
      window.fbq('track', metaName, params);
    }
  } catch (_) {
    // Never let analytics break the UI.
  }
};
